import { Editor } from '@tiptap/core';
import {
  EditorContent,
  JSONContent,
  Editor as ReactEditor,
} from '@tiptap/react';
import cx from 'classnames';
import { Button } from 'components/admin/ui/Button';
import { ConfirmationModal } from 'components/admin/ui/ConfirmationModal';
import { DropdownMenu, FormV2 } from 'components/ui';
import { Check, Link03 } from 'ethena-icons';
import { useToggle } from 'lib/hooks';
import _ from 'lodash';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import css from './RichTextEditor.module.scss';
import {
  getMenuItems,
  IMenuButtonConfiguration,
  IMenuSelectConfiguration,
  RichTextEditorMenuItemConfiguration,
} from './datasets';
import { useRichTextEditor } from './hooks';
import { RichTextEditorButton, RichTextEditorSelect } from './types';

interface IRichTextEditorContext {
  editor: Editor;
  id: string;
}

const RichTextEditorContext = createContext<IRichTextEditorContext>(null);

const useRichTextEditorContext = () =>
  useContext<IRichTextEditorContext>(RichTextEditorContext);

interface IRichTextEditorProps {
  children: ReactNode | ((context: IRichTextEditorContext) => ReactNode);
  maxLength?: number;
  onChange: (params: {
    json: JSONContent;
    wordCount: number;
    html: string;
  }) => void;
  placeholder?: string;
  value: JSONContent | string;
}

const RichTextEditor = ({
  children,
  maxLength,
  onChange,
  placeholder,
  value,
}: IRichTextEditorProps) => {
  const { editor, id } = useRichTextEditor({
    maxLength,
    onChange,
    placeholder,
    value,
  });

  if (!editor) {
    return null;
  }

  const contextValues = {
    editor,
    id,
  };

  return (
    <RichTextEditorContext.Provider value={contextValues}>
      {_.isFunction(children) ? children(contextValues) : children}
    </RichTextEditorContext.Provider>
  );
};

interface IContentProps {
  className?: string;
  isInvalid?: boolean;
}

const Content = ({ className, isInvalid }: IContentProps) => {
  const { editor, id } = useRichTextEditorContext();
  return (
    <EditorContent
      id={id}
      className={cx(css.tiptapEditor, className, { [css.invalid]: isInvalid })}
      editor={editor as ReactEditor}
    />
  );
};

interface IMenuProps {
  buttonClassName?: string;
  className?: string;
  orderedItems: RichTextEditorMenuItemConfiguration[];
}

const Menu = ({ buttonClassName, className, orderedItems }: IMenuProps) => {
  const { editor } = useRichTextEditorContext();

  const [isLinkModalVisible, toggleLinkModal] = useToggle(false);
  const [link, setLink] = useState<string>('');

  const handleSetLink = (href: string) => {
    if (href) {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    toggleLinkModal();
  };

  const handleSetLinkClick = (editor: Editor) => {
    setLink(editor.getAttributes('link').href);
    toggleLinkModal();
  };

  const renderMenuItem = (
    menuItem: IMenuButtonConfiguration | IMenuSelectConfiguration
  ) => {
    if (menuItem.id in RichTextEditorButton) {
      const {
        getIsDisabled,
        getIsSelected,
        icon: Icon,
        id,
        onClick,
      } = menuItem as IMenuButtonConfiguration;

      const handleClick = () => {
        const cb =
          id === RichTextEditorButton.SET_LINK ? handleSetLinkClick : undefined;
        onClick(editor, cb);
      };

      const isDisabled = getIsDisabled?.(editor);

      return (
        <button
          aria-disabled={isDisabled}
          className={cx(css.menuButton, buttonClassName, {
            [css.selected]: getIsSelected?.(editor),
          })}
          key={id}
          onClick={(ev) => (isDisabled ? ev.preventDefault() : handleClick())}
          type="button"
        >
          <Icon height={18} width={18} />
        </button>
      );
    }

    if (menuItem.id in RichTextEditorSelect) {
      const { id, options } = menuItem as IMenuSelectConfiguration;

      const selected =
        _.find(options, (x) => x.getIsSelected(editor)) || _.first(options);
      const isTextStyleSelect = id === RichTextEditorSelect.TEXT_STYLE;

      return (
        <DropdownMenu key={id}>
          <DropdownMenu.Toggle
            className={cx(css.menuSelectToggle, {
              [css.textStyle]: isTextStyleSelect,
            })}
          >
            {selected?.label}
          </DropdownMenu.Toggle>
          <DropdownMenu.Options
            aria-label="Text styles menu"
            className={cx(css.menuSelectOptions, {
              [css.textStyle]: isTextStyleSelect,
            })}
          >
            {_.map(options, (option) => (
              <DropdownMenu.Option
                className={cx(css.menuSelectOption, css[option.classNameKey])}
                key={option.id}
                onClick={() => option.onSelect(editor)}
              >
                {option.label}
                {option.getIsSelected(editor) && (
                  <Check
                    aria-hidden="true"
                    className={css.checkIcon}
                    height={12}
                    width={12}
                  />
                )}
              </DropdownMenu.Option>
            ))}
          </DropdownMenu.Options>
        </DropdownMenu>
      );
    }
  };

  const menuItems = useMemo(() => getMenuItems(orderedItems), [orderedItems]);

  return (
    <div className={cx(css.menu, className)}>
      {_.map(menuItems, renderMenuItem)}
      <LinkModal
        isVisible={isLinkModalVisible}
        onClose={toggleLinkModal}
        onConfirm={handleSetLink}
        value={link}
      />
    </div>
  );
};

interface ILinkModalProps {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: (link: string) => void;
  value: string;
}

const LinkModal = ({
  isVisible,
  onClose,
  onConfirm,
  value,
}: ILinkModalProps) => {
  const [link, setLink] = useState<string>(value);

  useEffect(() => setLink(value), [value, isVisible]);

  return (
    <ConfirmationModal isVisible={isVisible} onClose={onClose}>
      <ConfirmationModal.Header>
        <ConfirmationModal.Icon color="gray" icon={Link03} />
      </ConfirmationModal.Header>
      <ConfirmationModal.Body>
        <FormV2.Group>
          <FormV2.Label>Link</FormV2.Label>
          <FormV2.Control
            onChange={({ target }) => setLink(target.value)}
            placeholder="Link"
            value={link ?? ''}
          />
        </FormV2.Group>
      </ConfirmationModal.Body>
      <ConfirmationModal.Footer>
        <Button onClick={onClose} variant="outline">
          Cancel
        </Button>
        <Button onClick={() => onConfirm(link)}>Set link</Button>
      </ConfirmationModal.Footer>
    </ConfirmationModal>
  );
};

RichTextEditor.Content = Content;
RichTextEditor.Menu = Menu;

export { RichTextEditor };
export type { IMenuProps as IRichTextEditorMenuProps, IRichTextEditorProps };
